import { useState, useRef, useEffect } from "react";
import type { SelectOption } from "../types";

interface Props<T extends string> {
  options: SelectOption<T>[];
  value: T[];
  onChange: (v: T[]) => void;
  placeholder?: string;
  className?: string;
}

export function DropdownMultiSelect<T extends string>({
  options,
  value,
  onChange,
  placeholder = "Select...",
  className = "",
}: Props<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearch("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Focus search when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 0);
    } else {
      setSearch("");
    }
  }, [isOpen]);

  const filteredOptions = search.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  const allFilteredValues = filteredOptions.map((o) => o.value);
  const isAllSelected = allFilteredValues.length > 0 && allFilteredValues.every((v) => value.includes(v));
  const isSomeSelected = allFilteredValues.some((v) => value.includes(v)) && !isAllSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = isSomeSelected;
    }
  }, [isSomeSelected]);

  const handleSelectAll = () => {
    if (isAllSelected) {
      // deselect only the filtered ones
      onChange(value.filter((v) => !allFilteredValues.includes(v)));
    } else {
      // add all filtered that aren't already selected
      const toAdd = allFilteredValues.filter((v) => !value.includes(v));
      onChange([...value, ...toAdd]);
    }
  };

  const handleToggle = (val: T) => {
    onChange(value.includes(val) ? value.filter((v) => v !== val) : [...value, val]);
  };

  // Button label
  const totalSelected = value.length;
  let buttonText = placeholder;
  if (totalSelected > 0) {
    if (totalSelected === options.length) {
      buttonText = "All Selected";
    } else if (totalSelected === 1) {
      const selectedOpt = options.find((o) => o.value === value[0]);
      buttonText = selectedOpt ? selectedOpt.label : placeholder;
    } else {
      buttonText = `${totalSelected} Selected`;
    }
  }

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {/* Trigger */}
      <button
        type="button"
        className="dd-multiselect-trigger w-full flex items-center justify-between text-left text-sm whitespace-nowrap"
        onClick={() => setIsOpen(!isOpen)}
        style={{ minHeight: "38px" }}
      >
        <span className="truncate flex-1 overflow-hidden text-ellipsis" style={{ color: totalSelected > 0 ? "#3d3d3d" : "#9ca3af" }}>
          {buttonText}
        </span>
        <svg
          className={`w-4 h-4 ml-2 flex-shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Popover */}
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden" style={{ minWidth: "260px", width: "max-content", maxWidth: "360px" }}>
          {/* Search box */}
          <div className="dd-ms-search-row">
            <svg width="14" height="14" viewBox="0 0 20 20" fill="none" className="dd-ms-search-icon">
              <circle cx="9" cy="9" r="6" stroke="#9ca3af" strokeWidth="2" />
              <path d="M13.5 13.5L17 17" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              ref={searchRef}
              type="text"
              className="dd-ms-search-input"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            {search && (
              <button className="dd-ms-search-clear" onClick={() => setSearch("")} tabIndex={-1}>
                ✕
              </button>
            )}
          </div>

          <div className="max-h-52 overflow-y-auto">
            {/* Select All — scoped to filtered */}
            {filteredOptions.length > 0 && (
              <label className="flex items-center px-4 py-2.5 border-b border-gray-100 cursor-pointer hover:bg-gray-50 transition-colors">
                <input
                  ref={selectAllRef}
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={handleSelectAll}
                  className="w-4 h-4 mr-3 accent-orange-500 cursor-pointer rounded"
                />
                <span className="text-sm text-gray-700 font-medium" style={{ whiteSpace: "nowrap" }}>
                  {search ? `Select all "${search}"` : "Select All"}
                </span>
              </label>
            )}

            {/* Options */}
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-5 text-sm text-gray-400 text-center">No results</div>
            ) : (
              filteredOptions.map((opt, idx) => (
                <label
                  key={opt.value}
                  className={`flex items-center px-4 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors ${
                    idx < filteredOptions.length - 1 ? "border-b border-gray-50" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={value.includes(opt.value)}
                    onChange={() => handleToggle(opt.value)}
                    className="w-4 h-4 mr-3 accent-orange-500 cursor-pointer rounded"
                  />
                  <span className="text-sm text-gray-600" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{opt.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
